import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Check, Share2, Sparkles, UserPlus, Loader2, ChevronRight, Images } from "lucide-react";
import api from "../lib/api";

// The member's own referral hub: their personal key, a one-tap share, and their
// Resonance Points. Aggregate-only and private, no leaderboard, no ad price.
export const ResonancePoints = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);

  useEffect(() => {
    api.get("/referrals/me").then(({ data }) => setData(data)).catch(() => setData(false));
  }, []);

  if (data === null) return (
    <div className="border border-[#14A89A]/25 bg-[#14A89A]/[0.04] p-5 flex justify-center" data-testid="resonance-loading">
      <Loader2 className="animate-spin text-[#14A89A]" size={18} />
    </div>
  );
  if (data === false) return null;

  const { code, brought_in = 0, points = 0 } = data;
  const link = `${window.location.origin}/?ref=${code}`;
  const message =
    "I'm part of Sovereign Signal, a private, encrypted space for people building sovereign systems, trades, and lives. No algorithms, no clout, no noise. If it resonates, this is my key in:";

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Sovereign Signal", text: message, url: link });
        setShared(true);
        setTimeout(() => setShared(false), 1800);
      } catch { /* user dismissed the sheet, no-op */ }
    } else {
      await navigator.clipboard.writeText(`${message} ${link}`);
      setShared(true);
      setTimeout(() => setShared(false), 1800);
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="border border-[#14A89A]/30 bg-[#14A89A]/[0.05] p-5" data-testid="resonance-points">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={15} className="text-[#14A89A]" />
        <span className="font-heading text-sm text-white">Resonance Points</span>
      </div>
      <p className="font-body text-xs text-zinc-400 mb-4 leading-relaxed">
        Bring sovereigns you'd vouch for into the field. Every one who takes root strengthens the Signal, and points are yours to keep. We're actively building toward the many ways they'll serve you as we grow.
      </p>

      {/* Aggregate totals, private to you */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="border border-white/10 bg-black/30 px-4 py-3" data-testid="resonance-brought-in">
          <div className="flex items-center gap-1.5">
            <UserPlus size={13} className="text-zinc-500" />
            <span className="font-heading text-xl text-white">{brought_in}</span>
          </div>
          <div className="font-body text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">Brought into the Signal</div>
        </div>
        <div className="border border-white/10 bg-black/30 px-4 py-3" data-testid="resonance-total">
          <div className="flex items-center gap-1.5">
            <Sparkles size={13} className="text-[#14A89A]" />
            <span className="font-heading text-xl text-[#14A89A]">{points}</span>
          </div>
          <div className="font-body text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">Resonance Points</div>
        </div>
      </div>

      {/* Personal key */}
      <div className="flex items-center justify-between border border-white/15 px-3 py-2 mb-3">
        <span className="font-heading text-base tracking-[0.18em] text-[#14A89A] break-all" data-testid="resonance-key">{code}</span>
        <button onClick={copyLink} data-testid="resonance-copy-link" title="Copy your invite link" className="text-zinc-500 hover:text-white transition-colors shrink-0 ml-2">
          {copied ? <Check size={15} className="text-[#14A89A]" /> : <Copy size={15} />}
        </button>
      </div>

      {/* One-tap share */}
      <button onClick={share} data-testid="resonance-share-button" className="w-full flex items-center justify-center gap-2 bg-[#14A89A] text-[#0A0A0A] font-medium px-4 py-2.5 text-sm font-body hover:bg-[#2BC4B5] transition-colors">
        {shared ? <><Check size={15} /> Shared</> : <><Share2 size={15} /> Share your key</>}
      </button>
      <a href={`/about?ref=${code}`} target="_blank" rel="noopener noreferrer" data-testid="resonance-share-cards"
        className="mt-2 w-full flex items-center justify-center gap-2 border border-[#A855F7]/50 bg-[#A855F7]/10 text-[#C084FC] hover:bg-[#A855F7]/20 font-medium px-4 py-2.5 text-sm font-body transition-colors">
        <Images size={15} /> Share the Signal cards
      </a>
      <p className="mt-1.5 font-body text-[11px] text-zinc-500 leading-snug text-center">8 ready to post cards with your key on each one. Anyone who opens the page has your key filled in automatically.</p>

      {/* How points grow, clear + rewarding */}
      <div className="mt-4 border-t border-white/10 pt-3 space-y-1.5" data-testid="resonance-how">
        <p className="font-body text-[10px] uppercase tracking-wider text-zinc-500 mb-1">How points grow</p>
        <p className="font-body text-xs text-zinc-400 leading-snug">A sovereign you bring in is welcomed by the Council <span className="text-[#14A89A]">· +1</span></p>
        <p className="font-body text-xs text-zinc-400 leading-snug">They settle in, opening the app over their first days <span className="text-[#14A89A]">· +1</span></p>
        <p className="font-body text-xs text-zinc-400 leading-snug">They transmit their first signal <span className="text-[#14A89A]">· +1</span></p>
        <button onClick={() => navigate("/compass")} data-testid="resonance-how-link" className="mt-1 inline-flex items-center gap-1 font-body text-xs text-[#14A89A] hover:text-[#2BC4B5] transition-colors">
          How it works <ChevronRight size={13} />
        </button>
      </div>
    </div>
  );
};
